/** @jsxImportSource @emotion/react */
import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from "react-redux";
import { IAppState } from "../../../../core/redux/IAppState";
import { getResource } from "../../../../language/resources/getResource";
import { ColumnConfig } from "../../../../layout/table/columnConfig";
import { sortByDateProp, sortByProp } from "../../../../language/module";
import { ENTITLEMENT_SHARE_STATUS, EntitlementShare, FnFRedemptionSummary } from "../../models";
import { HistoricalRedemption } from "../historicalRedemption";
import Table from "../../../../layout/table/Table";
import { apostrophize } from '../../../../language/strings';
import { fullName } from '../../../module';
import { GuestDetails } from '../../../guestDetails';
import {formatDate} from '../../../../language/time';
import { Column, Row } from '../../../../layout/flexbox';
import { DARK_GREEN_CREEK, DARK_GREY, RED_CREEK, WHITE } from '../../../../styles/colors';
import { MediumText } from '../../../../styles/styles';
import pendingImage from '../../../../assets/icons/pending.svg';
import { css } from '@emotion/react';

const SHARED_REDEMPTION_TYPE = 5;
const REDEMPTION_SUMMARY_TYPE = 'Redemption';

const redemptionSummaryTitle = css`
  margin-left: 50px; 
  margin-bottom: 10px; 
  color: ${DARK_GREY};
  text-align: left;
`;

const redemptionSummaryTable = css`
  max-width: 95%
  overflow-y: auto;

  tbody tr {
      ${MediumText};
      background-color: ${WHITE} !important;
    }
  th {
    background-color: ${WHITE};
    color: ${DARK_GREY} !important;
  }
`;
const redeemQuantityContainer = css`
  width: 35px;
  padding: 5px 7px !important;
`;

const redeemQuantityBox = (quantity: number) => css`
  background-color: ${quantity >= 0 ? DARK_GREEN_CREEK : RED_CREEK};
  border-radius: 5px;
  text-align: center;
  padding: 3px 0px !important;
  color: WHITE;
`;

const pendingImageStyle = css`
  width: 15px;
  height: 15px;
  margin-right: 5px;
  margin-bottom: -3px;
`;

const overflowEllipsis = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    overflow: visible;
  }
`;


type Props = {
  guest: GuestDetails,
  entitlementShares: EntitlementShare[],
  isLoadingEntitlementShares: boolean,
  entitlementSharesError: boolean,
  redemptionHistory: HistoricalRedemption[],
};


/**
 * Friends and Family Redemption Summary component
 *
 * PureComponent: true
 * Connected to Redux: true
 */
export const FnFRedemptionSummaryTable = (props: Props) => {

  const resources = useSelector((state: IAppState) => state.locale);
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);

  const columnConfig = useMemo(() => [
  { id: 1, tooltipText: getResource(resources, 'FNF_REDEMPTION_SUMMARY_COLUMN_REDEEM_DATE'), title: getResource(resources, 'FNF_REDEMPTION_SUMMARY_COLUMN_REDEEM_DATE'), 
    onSort: sortByDateProp('redeemDate') as any, defaultSortingColumn: true, defaultSortingDirection: 'desc' },
  { id: 2, tooltipText: getResource(resources, 'FNF_REDEMPTION_SUMMARY_COLUMN_RESORT'), title: getResource(resources, 'FNF_REDEMPTION_SUMMARY_COLUMN_RESORT'), onSort: sortByProp('redeemResort', true) },
  { id: 3, tooltipText: '#', title: '#', onSort: sortByProp('redeemQuantity', true) },
  { id: 4, tooltipText: getResource(resources, 'FNF_REDEMPTION_SUMMARY_COLUMN_REF_ID'), title: getResource(resources, 'FNF_REDEMPTION_SUMMARY_COLUMN_REF_ID'), onSort: sortByProp('refID', true) },
  { id: 5, tooltipText: getResource(resources, 'FNF_REDEMPTION_SUMMARY_COLUMN_TYPE'), title: getResource(resources, 'FNF_REDEMPTION_SUMMARY_COLUMN_TYPE'), onSort: sortByProp('summaryType', true) },
] as Array<ColumnConfig<FnFRedemptionSummary>>, [resources]);

  const mapEntitlementShareToRedemptionSummary = useCallback((share: EntitlementShare, quantity: number, redemptionId: string): FnFRedemptionSummary => {
    if (!share) {
      return {} as FnFRedemptionSummary;
    }

    if (share.entitlementShareStatusId === ENTITLEMENT_SHARE_STATUS.SHARED) {
      return {
        externalShareId: share.externalShareId,
        redemptionId: redemptionId,
        redeemDate: share.shareUtcDateTime,
        redeemResort: getResource(resources, 'FNF_REDEMPTION_SUMMARY_PENDING'),
        redeemQuantity: quantity,
        refId: share.toEmailAddress,
        summaryType: ENTITLEMENT_SHARE_STATUS.SHARED.toString(),
        summaryTypeDescription: getResource(resources, 'FNF_REDEMPTION_SUMMARY_SHARED'),
      } as FnFRedemptionSummary;
    }

    if (share.entitlementShareStatusId === ENTITLEMENT_SHARE_STATUS.CLAIMED) {
      return {
        externalShareId: share.externalShareId,
        redemptionId: redemptionId,
        redeemDate: share.claimedUtcDateTime,
        redeemResort: getResource(resources, 'FNF_REDEMPTION_SUMMARY_ALL_RESORTS'),
        redeemQuantity: quantity,
        refId: share.externalShareId,
        summaryType: ENTITLEMENT_SHARE_STATUS.CLAIMED.toString(),
        summaryTypeDescription: getResource(resources, 'FNF_REDEMPTION_SUMMARY_CLAIMED'),
      } as FnFRedemptionSummary;
    }
    
    if (share.entitlementShareStatusId === ENTITLEMENT_SHARE_STATUS.REVOKED) {
      return {
        externalShareId: share.externalShareId,
        redemptionId: redemptionId,
        redeemDate: share.revokedUtcDateTime,
        redeemResort: share.claimedUtcDateTime ? getResource(resources, 'FNF_REDEMPTION_SUMMARY_ALL_RESORTS') : getResource(resources, 'FNF_REDEMPTION_SUMMARY_PENDING'),
        redeemQuantity: quantity,
        refId: share.claimedUtcDateTime ? share.externalShareId : share.toEmailAddress,
        summaryType: ENTITLEMENT_SHARE_STATUS.REVOKED.toString(),
        summaryTypeDescription: getResource(resources, 'FNF_REDEMPTION_SUMMARY_REVOKED'),
      } as FnFRedemptionSummary;
    }

    return {} as FnFRedemptionSummary;
  }, [resources]);

  const mapHistoricalRedemptionToRedemptionSummary = useCallback((redemption: HistoricalRedemption): FnFRedemptionSummary => {
    if (!redemption) {
      return {} as FnFRedemptionSummary;
    }

    // Find the corresponding entitlement share for the redemption based on the redemption external code
    if (redemption.redemptionTypeId === SHARED_REDEMPTION_TYPE && redemption.redemptionExternalCode) {
      const share = props.entitlementShares.find((share: EntitlementShare) => {
        return share.externalShareId.endsWith(redemption.redemptionExternalCode!);
      });
      if (share) {
        return mapEntitlementShareToRedemptionSummary(share, redemption.redeemQuantity, redemption.redemptionID.toString());
      }
    }

    return {
      redemptionId: redemption.redemptionID,
      redeemDate: redemption.redeemDate,
      redeemResort: redemption.redeemResort,
      redeemQuantity: redemption.redeemQuantity,
      refId: redemption.redemptionExternalCode,
      summaryType: REDEMPTION_SUMMARY_TYPE,
      summaryTypeDescription: getResource(resources, 'FNF_REDEMPTION_SUMMARY_REDEMPTION'),
    } as FnFRedemptionSummary;
  },[mapEntitlementShareToRedemptionSummary, props.entitlementShares, resources]);

  const redemptionSummaries = useMemo(() => {
    if (props.isLoadingEntitlementShares || !props.redemptionHistory?.length) {
      return [] as FnFRedemptionSummary[];
    }
    return props.redemptionHistory.map(mapHistoricalRedemptionToRedemptionSummary);
  }, [props.isLoadingEntitlementShares, props.redemptionHistory, mapHistoricalRedemptionToRedemptionSummary]);

  const renderRow = (row: FnFRedemptionSummary): React.ReactNode => {
      return (
      <tr key={`${row.redemptionId}${row.externalShareId}`}>
        <td width={1}>
          <Row>
            { 
              row.summaryType === ENTITLEMENT_SHARE_STATUS.SHARED.toString() &&
                <img css={pendingImageStyle} src={pendingImage} alt="" title={getResource(resources, 'FNF_REDEMPTION_SUMMARY_SHARED_DATE')} />
            }
            <span>{row.redeemDate ? formatDate(new Date(row.redeemDate), dateFormat) : null}</span>
          </Row>
        </td>
        <td>{row.redeemResort}</td>
        <td css={redeemQuantityContainer} title={row.redemptionId ? getResource(resources, 'FNF_REDEMPTION_SUMMARY_REDEMPTION_ID', row.redemptionId) : ''}>
          <div css={redeemQuantityBox(row.redeemQuantity)}>{row.redeemQuantity}</div>
        </td>
        <td>
          <span css={overflowEllipsis}>{row.refId}</span>
        </td>
        <td width={1}>
          <span css={css`margin: 0px 5px;`}>{row.summaryTypeDescription}</span>
        </td>
      </tr>
    )};

  return (
    <Column>
      <div css={redemptionSummaryTitle}>
        {getResource(resources, 'FNF_REDEMPTION_SUMMARY_LABEL')}
      </div>
      <Table 
        columns={columnConfig}
        rootCss={redemptionSummaryTable}
        data={redemptionSummaries}
        isLoading={props.isLoadingEntitlementShares}
        noDataMessage={getResource(resources, 'FNF_REDEMPTION_SUMMARY_NO_HISTORY')}
        renderRow={renderRow}
        error={
            props.entitlementSharesError
            ? getResource(resources, 'FNF_REDEMPTION_SUMMARY_FAILED_FETCH', apostrophize(fullName(props.guest)))
            : undefined
        }
      />
    </Column>
)};
